import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config';

const supportFaqs = [
  {
    question: "How long does support take?",
    answer: "We usually reply to support email inquiries and contact form submissions within 2 to 4 hours. Our WhatsApp live support is available during standard trading sessions."
  },
  {
    question: "Can I get help installing an EA?",
    answer: "Yes, absolutely. Every EA download contains a comprehensive user installation guide PDF and setup presets. If you run into issues, our support team can assist you step-by-step."
  },
  {
    question: "Do you offer refunds?",
    answer: "Due to the digital delivery and compiled nature of MetaTrader Expert Advisor files, all sales are final. However, if a file fails to load or has defects, we will fix it or replace it immediately."
  },
  {
    question: "Can I request custom trading bots?",
    answer: "Yes! You can request custom bots from MQL5 or specific algorithmic models. Check out our Request EA page, or use this contact page to outline your trading ideas."
  },
  {
    question: "Do you support MT4 and MT5?",
    answer: "Yes, we support both MetaTrader 4 and MetaTrader 5 architectures. Each product details page displays platform compatibilities prominently."
  }
];

const Contact = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });
    try {
      const res = await fetch(`${API_URL}/api/forms/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ name: '', email: '', subject: '', message: '', contact: '' });
        setTimeout(() => setStatus({ loading: false, success: false, error: null }), 5000);
      } else {
        setStatus({ loading: false, success: false, error: 'Failed to send message.' });
      }
    } catch (err) {
      console.error(err);
      setStatus({ loading: false, success: false, error: 'Network error.' });
    }
  };

  return (
    <main className="min-h-screen relative pt-24 pb-24">
      {/* Ambient background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 rounded-[100%] blur-[120px] -z-10 pointer-events-none"></div>
      
      <section className="max-w-7xl mx-auto px-margin-mobile md:px-margin-desktop">
        
        {/* Breadcrumbs */}
        <div className="flex items-center space-x-2 text-[12px] text-on-surface-variant mb-12">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span>/</span>
          <span className="text-silver-text">Contact</span>
        </div>

        {/* Header */}
        <div className="text-center mb-16">
          <div className="bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded inline-block mb-6 border border-primary/20">
            Get In Touch
          </div>
          <h1 className="font-display-lg text-[48px] md:text-[64px] font-bold text-silver-text mb-4 leading-tight">
            Contact AlgoForexEA
          </h1>
          <p className="text-[14px] text-on-surface-variant max-w-2xl mx-auto">
            Need help choosing an EA or requesting a specific strategy? Our support team is here to help.
          </p>
        </div>

        {/* 4 Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          
          {/* Card 1: Email */}
          <div className="bg-[#1A1B20] border border-white/5 rounded-2xl p-8 flex flex-col h-full">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-primary text-[20px]">mail</span>
            </div>
            <h3 className="text-[16px] font-bold text-silver-text mb-3">Email Support</h3>
            <p className="text-[13px] text-on-surface-variant leading-relaxed mb-6 flex-grow">
              Drop us an email for account queries, download issues, or technical setup issues.
            </p>
            <div>
              <a href="mailto:support@algoforexea.online" className="text-primary font-bold text-[14px] hover:underline block mb-1">
                support@algoforexea.online
              </a>
              <span className="text-[12px] text-on-surface-variant/60 italic">Average reply: 2-4 hours</span>
            </div>
          </div>

          {/* Card 2: WhatsApp */}
          <div className="bg-[#1A1B20] border border-white/5 rounded-2xl p-8 flex flex-col h-full">
            <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-[#25D366] text-[20px]">chat</span>
            </div>
            <h3 className="text-[16px] font-bold text-silver-text mb-3">WhatsApp Support</h3>
            <p className="text-[13px] text-on-surface-variant leading-relaxed mb-6 flex-grow">
              Speak directly with an agent for rapid technical support or order inquiries.
            </p>
            <a href="https://wa.me/919426532062?text=Hi!%20I%20would%20like%20to%20get%20in%20touch." target="_blank" rel="noreferrer" className="w-full text-center rounded-xl bg-[#25D366] text-white px-4 py-3 font-bold text-[13px] hover:opacity-90 transition-opacity mt-auto">
              Chat on WhatsApp
            </a>
          </div>

          {/* Card 3: Custom EA */}
          <div className="bg-[#1A1B20] border border-white/5 rounded-2xl p-8 flex flex-col h-full">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-purple-500 text-[20px]">arrow_back</span>
            </div>
            <h3 className="text-[16px] font-bold text-silver-text mb-3">Custom EA Requests</h3>
            <p className="text-[13px] text-on-surface-variant leading-relaxed mb-6 flex-grow">
              Looking for a specific trading robot or MT4/MT5 EA not listed in our catalog?
            </p>
            <Link to="/request" className="w-full text-center rounded-xl bg-transparent border border-white/10 text-silver-text px-4 py-3 font-bold text-[13px] hover:border-white/30 transition-colors mt-auto">
              Request EA File
            </Link>
          </div>

          {/* Card 4: Partnerships */}
          <div className="bg-[#1A1B20] border border-white/5 rounded-2xl p-8 flex flex-col h-full">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-blue-500 text-[20px]">group</span>
            </div>
            <h3 className="text-[16px] font-bold text-silver-text mb-3">Partnerships</h3>
            <p className="text-[13px] text-on-surface-variant leading-relaxed mb-6 flex-grow">
              Contact us for affiliate opportunities, reseller collaborations, or custom partnerships.
            </p>
            <div>
              <a href="mailto:support@algoforexea.online" className="text-silver-text font-bold text-[14px] hover:text-primary transition-colors block mb-1">
                support@algoforexea.online
              </a>
              <span className="text-[12px] text-on-surface-variant/60 italic">Corporate & Affiliate desk</span>
            </div>
          </div>

        </div>

        {/* Form and FAQ Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          
          {/* Left: Contact Form */}
          <div className="bg-[#1A1B20] border border-white/5 rounded-3xl p-8 md:p-10">
            <h2 className="text-[28px] font-bold text-silver-text mb-2">Send Support Message</h2>
            <p className="text-[14px] text-on-surface-variant mb-8">
              Fill out the form below and our technical help desk will reach out to you.
            </p>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[13px] font-bold text-silver-text block">Full Name <span className="text-red-500">*</span></label>
                  <input required name="name" value={formData.name} onChange={handleChange} type="text" placeholder="e.g. John Doe" className="w-full bg-[#131418] border border-white/5 rounded-xl px-4 py-3.5 text-[14px] text-silver-text focus:outline-none focus:border-primary/50 transition-colors placeholder:text-on-surface-variant/30" />
                </div>
                <div className="space-y-2">
                  <label className="text-[13px] font-bold text-silver-text block">Email Address <span className="text-red-500">*</span></label>
                  <input required name="email" value={formData.email} onChange={handleChange} type="email" placeholder="e.g. trader@example.com" className="w-full bg-[#131418] border border-white/5 rounded-xl px-4 py-3.5 text-[14px] text-silver-text focus:outline-none focus:border-primary/50 transition-colors placeholder:text-on-surface-variant/30" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[13px] font-bold text-silver-text block">Subject <span className="text-red-500">*</span></label>
                <input required name="subject" value={formData.subject} onChange={handleChange} type="text" placeholder="e.g. Installation help / EA query" className="w-full bg-[#131418] border border-white/5 rounded-xl px-4 py-3.5 text-[14px] text-silver-text focus:outline-none focus:border-primary/50 transition-colors placeholder:text-on-surface-variant/30" />
              </div>

              <div className="space-y-2">
                <label className="text-[13px] font-bold text-silver-text block">Message <span className="text-red-500">*</span></label>
                <textarea required name="message" value={formData.message} onChange={handleChange} rows="5" placeholder="Enter your message details here. Please include EA name or order numbers if applicable..." className="w-full bg-[#131418] border border-white/5 rounded-xl px-4 py-3.5 text-[14px] text-silver-text focus:outline-none focus:border-primary/50 transition-colors placeholder:text-on-surface-variant/30 resize-none"></textarea>
              </div>

              <button disabled={status.loading} type="submit" className="w-full rounded-xl bg-transparent border border-white/10 text-silver-text px-8 py-4 font-bold text-[15px] hover:border-primary hover:text-primary transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50">
                {status.loading && <span className="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>}
                Send Message <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
              </button>
            </form>
          </div>

          {/* Right: Support FAQs */}
          <div className="flex flex-col">
            <h2 className="text-[28px] font-bold text-silver-text mb-2">Support FAQs</h2>
            <p className="text-[14px] text-on-surface-variant mb-8">
              Review basic answers to help queries instantly.
            </p>

            <div className="space-y-4">
              {supportFaqs.map((faq, index) => {
                const isActive = activeFaq === index;
                return (
                  <div 
                    key={index}
                    className={`bg-[#1A1B20] border ${isActive ? 'border-primary/30' : 'border-white/5'} rounded-2xl overflow-hidden transition-colors duration-300`}
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left group"
                    >
                      <span className={`font-bold text-[14px] transition-colors duration-300 pr-4 ${isActive ? 'text-primary' : 'text-silver-text group-hover:text-primary'}`}>
                        {faq.question}
                      </span>
                      <div className="shrink-0 flex items-center justify-center w-6 h-6 rounded bg-white/5 text-on-surface-variant group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                        <span className="material-symbols-outlined text-[16px]">
                          {isActive ? 'remove' : 'add'}
                        </span>
                      </div>
                    </button>
                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="px-6 pb-6 pt-1 text-[13px] text-on-surface-variant leading-relaxed">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

      </section>
    </main>
  );
};

export default Contact;
